/**
 * Product Ranking Display
 * 各商品カードの左上にランキング番号を表示する機能
 * カスタム設定に対応
 */

(function() {
  'use strict';

  const DESKTOP_MEDIA_QUERY = window.matchMedia('(min-width: 750px)');
  const RANKING_SCROLL_SELECTOR =
    '.resource-list.force-full-width.resource-list__carousel slideshow-slides';

  function applyRanking(scope) {
    const root = scope || document;
    const productItems = root.querySelectorAll('.resource-list__item[data-product-ranking]');

    productItems.forEach((item) => {
      const ranking = item.getAttribute('data-product-ranking');
      if (!ranking) return;

      const cardGallery = item.querySelector('.card-gallery');
      if (!cardGallery) return;

      // 既存のランキングラベルを削除して再生成（gallery 内/外どちらに置かれていても）
      const existing = item.querySelector('.ranking-label');
      if (existing) existing.remove();

      const section = item.closest('[data-ranking-settings]');
      let settings = null;
      if (section) {
        try {
          const settingsJson = section.getAttribute('data-ranking-settings');
          if (settingsJson) {
            settings = JSON.parse(settingsJson);
          }
        } catch (e) {
          console.warn('Failed to parse ranking settings:', e);
        }
      }

      const rankingLabel = createRankingLabel(ranking, settings);
      const badgeStyle = (settings && settings.badgeStyle) || 'number';
      if (badgeStyle === 'text') {
        // テキストスタイル: 画像の「上」に配置（gallery の前にsiblingで挿入）
        const host = cardGallery.parentNode || cardGallery;
        host.insertBefore(rankingLabel, cardGallery);
      } else {
        // 三角/矩形バッジ: 画像左上にオーバーレイ
        cardGallery.insertBefore(rankingLabel, cardGallery.firstChild);
      }
    });
  }

  function getRankingSections(scope) {
    const root = scope || document;

    if (root instanceof Element && root.matches('[data-testid="product-list-ranking"]')) {
      return [root];
    }

    return Array.from(root.querySelectorAll('[data-testid="product-list-ranking"]'));
  }

  function getDesktopOffset(section) {
    const styles = window.getComputedStyle(section);
    const offset = parseFloat(styles.getPropertyValue('--util-page-margin-offset'));
    return Number.isFinite(offset) ? offset : 0;
  }

  function setInitialCarouselOffset(panelOrSection) {
    if (!DESKTOP_MEDIA_QUERY.matches || !(panelOrSection instanceof Element)) return;

    const section = panelOrSection.closest('[data-testid="product-list-ranking"]') || panelOrSection;
    if (!(section instanceof Element)) return;

    const scroller = panelOrSection.querySelector(RANKING_SCROLL_SELECTOR);
    if (!(scroller instanceof HTMLElement)) return;

    const offset = getDesktopOffset(section);
    scroller.scrollLeft = offset;
  }

  function initializeRankingCarouselOffset(scope) {
    const sections = getRankingSections(scope);

    sections.forEach((section) => {
      const activePanel = section.querySelector('.collection-tabs__panel--active');
      if (activePanel) {
        setInitialCarouselOffset(activePanel);
        return;
      }

      setInitialCarouselOffset(section);
    });
  }

  function scheduleRankingCarouselOffset(scope) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        initializeRankingCarouselOffset(scope);
      });
    });
  }

  function createRankingLabel(ranking, settings) {
    const label = document.createElement('div');
    const rankNum = parseInt(ranking, 10);
    const badgeStyle = (settings && settings.badgeStyle) || 'number';

    label.className = 'ranking-label ranking-label--' + badgeStyle;
    label.setAttribute('aria-label', `ランキング ${ranking}位`);

    if (settings && badgeStyle === 'text') {
      // 画像の上にシンプルな「No.X」テキスト（オーバーレイしない）
      label.textContent = 'No.' + rankNum;
      label.style.display = 'block';
      label.style.textAlign = 'left';
      label.style.fontSize = `${settings.fontSize ?? 18}px`;
      label.style.fontFamily = 'var(--font-money--family)';
      label.style.fontWeight = settings.fontWeight || '400';
      label.style.color = settings.simpleColor || 'var(--color-foreground)';
      label.style.lineHeight = '1';
      label.style.marginBottom = '8px';
      label.style.pointerEvents = 'none';
      return label;
    }

    if (settings && badgeStyle === 'triangle') {
      // 案A: 三角形バッジ（左上コーナー）。背景の三角だけクリップし、No.X 文字は
      // クリップしない別要素にして斜辺で切れないようにする
      const rankKey = rankNum <= 3 ? `rank${rankNum}` : 'rank4plus';
      const rankSettings = settings[rankKey] || {};
      const size = settings.triangleSize ?? 44;
      const triFont = Math.max(9, Math.round(size * 0.22));

      label.style.position = 'absolute';
      label.style.top = '0';
      label.style.left = '0';
      label.style.zIndex = '10';
      label.style.width = `${size}px`;
      label.style.height = `${size}px`;
      label.style.pointerEvents = 'none';

      // 背景の三角（クリップ）
      const tri = document.createElement('span');
      tri.style.position = 'absolute';
      tri.style.inset = '0';
      tri.style.clipPath = 'polygon(0 0, 100% 0, 0 100%)';
      tri.style.backgroundColor = rankSettings.bgColor || '#e4bcaa';
      label.appendChild(tri);

      // No.X テキスト（クリップしない＝斜辺で切れない）
      // 「No.」は小さめ・数字を大きく。左上コーナー（三角形の最も広い部分）に寄せて
      // 斜辺からはみ出さないようにする
      const txt = document.createElement('span');
      txt.style.position = 'absolute';
      txt.style.top = `${Math.round(size * 0.06)}px`;
      txt.style.left = `${Math.round(size * 0.09)}px`;
      txt.style.lineHeight = '1';
      txt.style.whiteSpace = 'nowrap';
      txt.style.display = 'inline-flex';
      // flex-end だと行ボックス底辺揃えになり、フォントサイズ差のディセント分だけ
      // 数字が「No.」より浮く。baseline でグリフの下端を揃える
      txt.style.alignItems = 'baseline';
      txt.style.fontFamily = 'var(--font-money--family)';
      txt.style.fontWeight = settings.fontWeight || '400';
      txt.style.color = rankSettings.textColor || '#ffffff';

      const pre = document.createElement('span');
      pre.textContent = 'No.';
      pre.style.fontSize = `${Math.round(triFont * 0.82)}px`;
      pre.style.lineHeight = '1';

      const num = document.createElement('span');
      num.textContent = rankNum;
      // 数字は大きく。ただし桁数が増えたら少し縮めて斜辺内に収める
      const numScale = String(rankNum).length >= 2 ? 1.2 : 1.6;
      num.style.fontSize = `${Math.round(triFont * numScale)}px`;
      num.style.lineHeight = '1';
      num.style.marginLeft = '0';
      // Inter は数字が等幅(tnum)既定のため「1」等の細い数字の左に空白ができ
      // 右に寄って見える。プロポーショナル幅にして No. との間隔を一定にする
      num.style.fontVariantNumeric = 'proportional-nums';

      txt.appendChild(pre);
      txt.appendChild(num);
      label.appendChild(txt);
      return label;
    }

    // 案B: 画像の枠上に数字（矩形バッジ）
    label.textContent = ranking;

    if (settings) {
      const rankKey = rankNum <= 3 ? `rank${rankNum}` : 'rank4plus';
      const rankSettings = settings[rankKey] || {};

      const paddingValue = settings.padding ?? 8;

      label.style.position = 'absolute';
      label.style.top = '0';
      label.style.left = '0';
      label.style.zIndex = '10';
      label.style.display = 'inline-flex';
      label.style.alignItems = 'center';
      label.style.justifyContent = 'center';
      label.style.fontSize = `${settings.fontSize ?? 14}px`;
      label.style.fontFamily = settings.fontFamily || 'var(--font-body--family)';
      label.style.fontWeight = settings.fontWeight || '600';
      const br = settings.borderRadius ?? 4;
      label.style.borderRadius = `0 0 ${br}px 0`;
      label.style.paddingTop = `${paddingValue}px`;
      label.style.paddingRight = `${paddingValue}px`;
      label.style.paddingBottom = `${paddingValue}px`;
      label.style.paddingLeft = `${paddingValue}px`;
      label.style.border = rankSettings.borderWidth !== undefined
        ? `${rankSettings.borderWidth}px solid ${rankSettings.borderColor || 'transparent'}`
        : 'none';
      label.style.color = rankSettings.textColor || 'var(--color-foreground)';
      label.style.backgroundColor = rankSettings.bgColor || '#f5f5f5';
      label.style.boxSizing = 'border-box';
      label.style.lineHeight = '1';
    } else {
      let rankClass = 'default';
      if (rankNum === 1) rankClass = '1';
      else if (rankNum === 2) rankClass = '2';
      else if (rankNum === 3) rankClass = '3';

      label.className = `ranking-label ranking-label--${rankClass}`;
    }

    return label;
  }

  // 初回実行
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      applyRanking();
      scheduleRankingCarouselOffset(document);
    });
  } else {
    applyRanking();
    scheduleRankingCarouselOffset(document);
  }

  // カスタマイザーでセクションが再レンダリングされた場合に再適用
  document.addEventListener('shopify:section:load', (event) => {
    applyRanking(event.target);
    scheduleRankingCarouselOffset(event.target);
  });

  // タブの遅延パネル（<template>）が展開されたとき。
  // 初回適用は DOMContentLoaded の1回だけなので、後から入るカードには自分で当てにいく。
  // 発火元: collection-tabs.js の #hydratePanel / section 側のフォールバック
  document.addEventListener('ranking:panel-hydrated', (event) => {
    const root = event.detail?.root;
    if (!root) return;
    applyRanking(root);
    scheduleRankingCarouselOffset(root);
  });

  // カスタマイザーで設定変更された場合にも再適用
  // (セクション全体の再レンダリングではなく、属性のみ変わるケースに対応)
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-ranking-settings') {
        applyRanking(mutation.target);
      }
    }
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['data-ranking-settings'],
    subtree: true,
  });

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-testid="product-list-ranking"] .collection-tabs__tab');
    if (!tab) return;

    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    const panel = document.getElementById(panelId);
    if (!panel) return;

    scheduleRankingCarouselOffset(panel);
  });

  const handleDesktopChange = () => {
    scheduleRankingCarouselOffset(document);
  };

  if (typeof DESKTOP_MEDIA_QUERY.addEventListener === 'function') {
    DESKTOP_MEDIA_QUERY.addEventListener('change', handleDesktopChange);
  } else if (typeof DESKTOP_MEDIA_QUERY.addListener === 'function') {
    DESKTOP_MEDIA_QUERY.addListener(handleDesktopChange);
  }

  // 共通カスタムスクロールバー (assets/custom-scrollbar.js) を ranking に
  // 適用するためのマーカー付与。
  function markScrollbars() {
    document.querySelectorAll('[data-testid="product-list-ranking"]').forEach((section) => {
      section.setAttribute('data-scrollbar-hover', '');
      section.querySelectorAll(RANKING_SCROLL_SELECTOR).forEach((scroller) => {
        scroller.setAttribute('data-custom-scrollbar', '');
      });
    });
    if (window.CustomScrollbar) window.CustomScrollbar.scan(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markScrollbars);
  } else {
    markScrollbars();
  }
  setTimeout(markScrollbars, 1500);
})();
